import React, { useState, useRef, useEffect } from 'react';
import { MapStyle } from './MapStyleDialog';
import './MapStyleAutocomplete.css';

interface MapStyleAutocompleteProps {
  mapStyles: MapStyle[];
  value: MapStyle;
  onChange: (style: MapStyle) => void;
  placeholder?: string;
}

const MapStyleAutocomplete: React.FC<MapStyleAutocompleteProps> = ({
  mapStyles,
  value,
  onChange,
  placeholder = 'Select a map style...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Include custom style if current value is custom
  const allStyles = value.name === 'Custom' && !mapStyles.find(s => s.name === 'Custom')
    ? [...mapStyles, value]
    : mapStyles;

  const filteredStyles = allStyles.filter(style => 
    style.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    style.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (style: MapStyle) => {
    onChange(style);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      setIsOpen(true);
      e.preventDefault();
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredStyles.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredStyles.length) {
          handleSelect(filteredStyles[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="map-style-autocomplete" ref={dropdownRef}>
      <div className="autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="autocomplete-input"
          value={isOpen ? searchTerm : value.name}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
        <button
          className="autocomplete-toggle"
          onClick={() => setIsOpen(!isOpen)}
          tabIndex={-1}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      {isOpen && filteredStyles.length > 0 && (
        <div className="autocomplete-dropdown">
          {filteredStyles.map((style, index) => (
            <div
              key={style.name}
              className={`autocomplete-option ${
                index === highlightedIndex ? 'highlighted' : ''
              } ${style.name === value.name ? 'selected' : ''}`}
              onClick={() => handleSelect(style)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="option-name">{style.name}</div>
              <div className="option-url">{style.url}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapStyleAutocomplete;